class BundleBuilder extends HTMLElement {
    constructor() {
        super();
        this.addToCartButton = this.querySelector('[data-add-bundle]');
        this.addBundleWrap = this.querySelector('[data-add-bundle-wrap]');
        this.quantityInputs = this.querySelectorAll('[data-bundle-quantity]');
        this.handleClick = this.handleClick.bind(this);
        this.handleInput = this.handleInput.bind(this);
        this._previewState = [];

        this.quantityInputs.forEach((input) => {
            input.addEventListener('change', () => {
                this.toggleAddToCartButton();
                this.updateSubscriptionPreview();
            });
        });
    }

    connectedCallback() {
        this.addEventListener('click', this.handleClick);
        this.addEventListener('input', this.handleInput);
        this.addToCartButton?.addEventListener('click', () => this.onAddToCartClick());
        this.addBundleWrap?.addEventListener('click', () => {
            if (this.addToCartButton?.disabled) {
                const targetTop = this.getBoundingClientRect().top + window.scrollY - 100;
                window.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
            }
        });

        this.querySelectorAll('input[name="subscription_choise"]').forEach((radio) => {
            radio.addEventListener('change', () => this.updateSubscriptionPreview());
        });

        // The whole monthly subscription box selects the subscription, not only its top row.
        // The "Skip, modify or cancel anytime" bar and its "Learn more" button keep their own behaviour.
        const subscriptionBox = this.querySelector('[data-subscription-box]');
        const subscriptionRadio = subscriptionBox?.querySelector('input[type="radio"]');
        if (subscriptionBox && subscriptionRadio) {
            subscriptionBox.addEventListener('click', (e) => {
                if (e.target.closest('label, input, a, button, [data-subscription-note]')) return;
                if (!subscriptionRadio.checked) subscriptionRadio.click();
            });
        }


        this.initStickyBar();
        this.initStickyVisibility();

        requestAnimationFrame(() => this.updateSubscriptionPreview());
        // The promo can switch on after load (cart check in bundle-promo-init); refresh the prices then.
        document.addEventListener('bundle-promo:active', () => this.updateSubscriptionPreview());
    }

    formatMoney(cents) {
        if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
            return Shopify.formatMoney(cents);
        }
        return '';
    }

    getSubscriptionDiscountPercent(totalPacks) {
        if (totalPacks >= 3) return 0.15;
        if (totalPacks === 2) return 0.1;
        if (totalPacks >= 1) return 0.05;
        return 0;
    }

    syncQuantityInputActive(input) {
        const parent = input.parentElement;
        if (!parent) return;
        const val = parseInt(input.value || 0, 10);
        if (val > 0) parent.setAttribute('data-active', '');
        else parent.removeAttribute('data-active');
    }

    getPricingState() {
        let originalCents = 0;
        let totalPacks = 0;
        let singlePackPriceCents = 0;

        this.quantityInputs.forEach((input) => {
            const qty = parseInt(input.value || 0, 10);
            if (qty <= 0) return;

            const card = input.closest('[data-subscription-card]');
            if (!card) return;

            const priceCents = parseInt(card.getAttribute('data-price-cents') || 0, 10);

            originalCents += priceCents * qty;
            totalPacks += qty;
            singlePackPriceCents = priceCents;
        });

        if (!singlePackPriceCents) {
            const firstCard = this.querySelector('[data-subscription-card]');
            if (firstCard) {
                singlePackPriceCents = parseInt(firstCard.getAttribute('data-price-cents') || 0, 10);
            }
        }

        const subscriptionSelected = this.querySelector('#subscription')?.checked;
        const currentDiscount = this.getSubscriptionDiscountPercent(totalPacks);
        const nextTotalPacks = totalPacks + 1;
        const nextDiscount = this.getSubscriptionDiscountPercent(nextTotalPacks);
        const nextDiscountedUnitPrice = Math.round(
            singlePackPriceCents * (1 - nextDiscount)
        );

        return {
            totalPacks,
            subscriptionSelected,
            singlePackPriceCents,
            nextDiscount,
            nextDiscountedUnitPrice
        };
    }

    // The "+N" badge next to the preview squares: pops in softly, gives a small bump when the number changes,
    // and shrinks away when it is no longer needed
    updatePreviewCount(badge, valueEl, extra) {
        const animate = !window.matchMedia('(prefers-reduced-motion: reduce)').matches && 'animate' in badge;
        const wasShown = !badge.hidden && badge.dataset.leaving !== 'true';
        const previous = valueEl.textContent;

        if (extra > 0) {
            badge.getAnimations().forEach((a) => a.cancel());
            badge.dataset.leaving = 'false';
            valueEl.textContent = extra;
            badge.hidden = false;
            if (!animate) return;
            if (!wasShown) {
                badge.animate([{ opacity: 0, transform: 'scale(0.6)' }, { opacity: 1, transform: 'scale(1)' }], {
                    duration: 220,
                    easing: 'cubic-bezier(0.34, 1.4, 0.64, 1)',
                });
            } else if (previous !== String(extra)) {
                badge.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.15)' }, { transform: 'scale(1)' }], {
                    duration: 220,
                    easing: 'ease-in-out',
                });
            }
            return;
        }

        if (!wasShown) return;
        if (!animate) {
            badge.hidden = true;
            return;
        }
        badge.dataset.leaving = 'true';
        badge.animate([{ opacity: 1, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(0.6)' }], {
            duration: 160,
            easing: 'ease-in',
            fill: 'forwards',
        }).onfinish = () => {
            if (badge.dataset.leaving !== 'true') return;
            badge.hidden = true;
            badge.dataset.leaving = 'false';
            badge.getAnimations().forEach((a) => a.cancel());
        };
    }

    // Fills one preview square with a product image or its default discount label.
    // Only a square that changes is touched: a new image fades in, a removed image crossfades into the label.
    renderPreviewSlot(slot, src, template) {
        const current = slot.dataset.previewSrc || '';
        if (current === src) return;
        slot.dataset.previewSrc = src;

        const animate = !window.matchMedia('(prefers-reduced-motion: reduce)').matches && 'animate' in slot;
        const fadeIn = (el, from, duration, easing) => {
            if (animate && el) {
                el.animate([{ opacity: 0, transform: from }, { opacity: 1, transform: 'none' }], {
                    duration,
                    easing,
                    fill: 'backwards',
                });
            }
        };
        const showDefault = () => {
            slot.innerHTML = `<div class="p-1">${slot.dataset.previewDefault || ''}</div>`;
        };

        if (src) {
            const fragment = template.content.cloneNode(true);
            const imgEl = fragment.querySelector('img');
            if (imgEl) imgEl.src = src;
            if (!animate || !imgEl) {
                slot.innerHTML = '';
                slot.appendChild(fragment);
                return;
            }
            // Crossfade, the same calm pace as removing: the new image fades in on top while what was there fades out
            const previous = Array.from(slot.children);
            slot.style.position = 'relative';
            imgEl.style.cssText = 'position:absolute;inset:0;';
            slot.appendChild(fragment);
            fadeIn(imgEl, 'none', 250, 'ease-in-out');
            previous.forEach((el) => {
                el.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 250, easing: 'ease-in-out', fill: 'forwards' }).onfinish = () => {
                    el.remove();
                };
            });
            return;
        }

        const images = slot.querySelectorAll('img');
        const oldImg = images[images.length - 1];
        if (!animate || !oldImg) {
            showDefault();
            return;
        }
        // Crossfade: the discount label fades in underneath while the image fades out on top of it
        showDefault();
        slot.style.position = 'relative';
        oldImg.style.cssText = 'position:absolute;inset:0;';
        slot.appendChild(oldImg);
        fadeIn(slot.firstElementChild, 'none', 250, 'ease-in-out');
        oldImg.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 250, easing: 'ease-in-out', fill: 'forwards' }).onfinish = () => {
            oldImg.remove();
        };
    }

    // Kortingsactie banner (snippets/bundle-promo-banner.liquid): fills the steps as packs are picked.
    // The promo only counts for one-time purchases, so the subscription choice gets its own message.
    updateBundlePromo(totalPacks, subscriptionSelected) {
        const banner = this.querySelector('[data-bundle-promo-banner]');
        if (!banner) return;

        const unlocked = !subscriptionSelected && totalPacks >= 3;
        let subtitleKey = 'subtitle-0';
        if (subscriptionSelected) subtitleKey = 'subtitle-subscription';
        else if (unlocked) subtitleKey = 'subtitle-unlocked';
        else if (totalPacks > 0) subtitleKey = `subtitle-${totalPacks}`;

        const titleEl = banner.querySelector('[data-bundle-promo-title]');
        const subtitleEl = banner.querySelector('[data-bundle-promo-subtitle]');
        const title = unlocked ? banner.dataset.titleUnlocked : banner.dataset.title;
        const subtitle = banner.getAttribute(`data-${subtitleKey}`);
        if (titleEl && title && titleEl.textContent !== title) titleEl.textContent = title;
        if (subtitleEl && subtitle && subtitleEl.textContent !== subtitle) subtitleEl.textContent = subtitle;

        banner.toggleAttribute('data-unlocked', unlocked);
        banner.toggleAttribute('data-subscription-selected', !!subscriptionSelected);
        banner.querySelectorAll('[data-bundle-promo-step]').forEach((step, index) => {
            step.toggleAttribute('data-filled', !subscriptionSelected && totalPacks > index);
        });
    }

    // Value of the packs the BUNDEL promo makes free: 1 free pack per 3 packs (3 -> 1, 6 -> 2, ...),
    // taking the cheapest selected packs, like Shopify's discount. Only for visitors who came via the
    // discount link (see snippets/bundle-promo-init.liquid). 0 otherwise.
    getBundlePromoFreeCents(totalPacks) {
        const freePacks = Math.floor(totalPacks / 3);
        if (freePacks < 1 || !window.bundlePromo?.active) return 0;
        if (!this.querySelector('[data-bundle-promo-banner]')) return 0;

        const prices = [];
        this.quantityInputs.forEach((input) => {
            const qty = parseInt(input.value || 0, 10);
            if (qty <= 0) return;
            const card = input.closest('[data-subscription-card]');
            const priceCents = parseInt(card?.getAttribute('data-price-cents') || 0, 10);
            for (let i = 0; i < qty; i++) prices.push(priceCents);
        });
        prices.sort((a, b) => a - b);
        return prices.slice(0, freePacks).reduce((sum, cents) => sum + cents, 0);
    }

    updateSubscriptionPreview() {
        this.quantityInputs.forEach((input) => this.syncQuantityInputActive(input));

        let originalCents = 0;
        let totalCans = 0;
        let totalPacks = 0;

        this.quantityInputs.forEach((input) => {
            const qty = parseInt(input.value || 0, 10);
            if (qty <= 0) return;

            const card = input.closest('[data-subscription-card]');
            if (!card) return;

            const priceCents = parseInt(card.getAttribute('data-price-cents') || 0, 10);
            const packQty = parseInt(card.getAttribute('data-pack-quantity') || 0, 10);

            originalCents += priceCents * qty;
            totalCans += packQty * qty;
            totalPacks += qty;
        });

        const discountPercent = this.getSubscriptionDiscountPercent(totalPacks);
        const discountedCents = Math.round(originalCents * (1 - discountPercent));

        const pricePerCanCents = totalCans > 0 ? Math.round(discountedCents / totalCans) : 0;
        const oneTimePricePerCanCents = totalCans > 0 ? Math.round(originalCents / totalCans) : 0;

        const elOneTimePrice = this.querySelector('[data-one-time-price]');
        const elOneTimePackQty = this.querySelector('[data-one-time-pack-quantity]');
        const elOneTimePackPrice = this.querySelector('[data-one-time-pack-quantity-price]');
        const elOriginal = this.querySelector('[data-sub-original-price]');
        const elDiscounted = this.querySelector('[data-sub-discounted-price]');
        const elPackQty = this.querySelector('[data-sub-pack-quantity]');
        const elPackPrice = this.querySelector('[data-sub-pack-quantity-price]');

        const defaultsEl = this.querySelector('[data-default-original-cents]');
        const useDefaults = totalPacks === 0 && defaultsEl;

        const displayOriginalCents = useDefaults ? parseInt(defaultsEl.dataset.defaultOriginalCents || 0, 10) : originalCents;
        const displayDiscountedCents = useDefaults ? parseInt(defaultsEl.dataset.defaultDiscountedCents || 0, 10) : discountedCents;
        const displayPackCans = useDefaults ? parseInt(defaultsEl.dataset.defaultPackCans || 0, 10) : totalCans;
        const displayPricePerCanCents = useDefaults && displayPackCans > 0
            ? Math.round(displayDiscountedCents / displayPackCans)
            : pricePerCanCents;
        const displayOneTimePricePerCanCents = useDefaults && displayPackCans > 0
            ? Math.round(displayOriginalCents / displayPackCans)
            : oneTimePricePerCanCents;

        // BUNDEL promo (3 halen, 2 betalen): from 3 one-time packs the cheapest pack is free.
        const promoFreeCents = this.getBundlePromoFreeCents(totalPacks);
        const oneTimeCents = originalCents - promoFreeCents;
        const elOneTimeOriginal = this.querySelector('[data-one-time-original-price]');
        if (elOneTimeOriginal) {
            elOneTimeOriginal.hidden = promoFreeCents <= 0;
            if (promoFreeCents > 0) elOneTimeOriginal.textContent = this.formatMoney(originalCents);
        }

        if (elOneTimePrice) elOneTimePrice.textContent = this.formatMoney(useDefaults ? displayOriginalCents : oneTimeCents);
        if (elOneTimePackQty) elOneTimePackQty.textContent = useDefaults ? displayPackCans : totalCans;
        if (elOneTimePackPrice) {
            const perCan = !useDefaults && totalCans > 0 ? Math.round(oneTimeCents / totalCans) : displayOneTimePricePerCanCents;
            elOneTimePackPrice.textContent = this.formatMoney(perCan);
        }
        if (elOriginal) elOriginal.textContent = this.formatMoney(displayOriginalCents);
        if (elDiscounted) elDiscounted.textContent = this.formatMoney(displayDiscountedCents);
        if (elPackQty) elPackQty.textContent = displayPackCans;
        if (elPackPrice) elPackPrice.textContent = this.formatMoney(displayPricePerCanCents);

        const subscriptionSelected = this.querySelector('#subscription')?.checked;
        const finalPriceCents = subscriptionSelected ? discountedCents : oneTimeCents;

        this.updateBundlePromo(totalPacks, subscriptionSelected);

        const elFinalPrice = this.querySelector('[data-final-price]');
        if (elFinalPrice) {
            if (totalPacks > 0) {
                elFinalPrice.textContent = this.formatMoney(finalPriceCents);
                elFinalPrice.hidden = false;
            } else {
                elFinalPrice.hidden = true;
            }
        }

        this.addToCartButton?.classList.toggle('space-between', totalPacks > 0);

        const elDiscountPercent = this.querySelector('[data-subscription-discount-percent]');
        if (elDiscountPercent) {
            const percentLabel =
                totalPacks >= 3 ? '15%' :
                    totalPacks === 2 ? '10%' :
                        totalPacks === 1 ? '5%' :
                            totalPacks === 0 ? '0%' : '0%';

            elDiscountPercent.textContent = percentLabel;
        }

        const upsellRoot = this.querySelector('[data-subscription-upsell-message]');
        if (upsellRoot) {
            const elTier = upsellRoot.querySelector('[data-subscription-discount-tier]');
            const elPackWord = upsellRoot.querySelector('[data-subscription-pack-word]');
            const elNextPercent = upsellRoot.querySelector('[data-subscription-next-tier-percent]');

            if (totalPacks >= 3) {
                upsellRoot.hidden = true;
            } else {
                upsellRoot.hidden = false;
                if (elTier) elTier.textContent = '1';
                if (elPackWord) elPackWord.textContent = totalPacks === 0 ? window.bundleStrings.pack_label : window.bundleStrings.more_label;
                if (elNextPercent) {
                    const nextPercent =
                        totalPacks === 0 ? '5%' :
                            totalPacks === 1 ? '10%' : '15%';

                    elNextPercent.textContent = nextPercent;
                }
            }
        }

        const previewTemplate = this.querySelector('[data-preview-template]');
        const previewItems = this.querySelectorAll('[data-preview="item"]');
        const previewCountEl = this.querySelector('[data-preview-count]');
        const previewCountValueEl = this.querySelector('[data-preview-count-value]');

        this.quantityInputs.forEach((input) => {
            const qty = parseInt(input.value || 0, 10);
            const card = input.closest('[data-subscription-card]');
            if (!card) return;

            const variantId = input.getAttribute('data-variant-id');
            const img = card.getAttribute('data-preview-image') || '';

            const currentQty = this._previewState.filter((item) => item.variantId === variantId).length;
            const diff = qty - currentQty;

            if (diff > 0) {
                for (let i = 0; i < diff; i++) {
                    this._previewState.push({ variantId, img });
                }
            }

            if (diff < 0) {
                let removeCount = Math.abs(diff);
                this._previewState = this._previewState.filter((item) => {
                    if (item.variantId === variantId && removeCount > 0) {
                        removeCount--;
                        return false;
                    }
                    return true;
                });
            }
        });

        const imagesToShow = this._previewState.slice(0, 3);

        if (previewTemplate && previewItems.length) {
            previewItems.forEach((slot, index) => {
                const data = imagesToShow[index];
                this.renderPreviewSlot(slot, data && data.img ? data.img : '', previewTemplate);
            });
        }

        if (previewCountEl && previewCountValueEl) {
            this.updatePreviewCount(previewCountEl, previewCountValueEl, Math.max(0, this._previewState.length - 3));
        }
    }

    handleClick(event) {
        const decreaseBtn = event.target.closest('[data-quantity-decrease]');
        const increaseBtn = event.target.closest('[data-quantity-increase]');
        if (!decreaseBtn && !increaseBtn) return;

        const card = event.target.closest('[data-subscription-card]');
        if (!card) return;

        const input = card.querySelector('[data-bundle-quantity]');
        if (!input) return;

        const min = parseInt(input.min || 0, 10);
        const max = input.max !== undefined && input.max !== '' ? parseInt(input.max, 10) : Infinity;
        const step = parseInt(input.step || 1, 10);
        const current = parseInt(input.value || 0, 10);

        if (decreaseBtn) input.value = Math.max(min, current - step);
        if (increaseBtn) input.value = Math.min(max, current + step);

        input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    handleInput(event) {
        const input = event.target.closest('[data-bundle-quantity]');
        if (!input) return;

        const min = parseInt(input.min || 0, 10);
        const max = input.max !== undefined && input.max !== '' ? parseInt(input.max, 10) : Infinity;
        const value = parseInt(input.value || 0, 10);

        if (isNaN(value) || value < min) {
            input.value = min;
        } else if (value > max) {
            input.value = max;
        }

        this.updateSubscriptionPreview();
    }

    initStickyBar() {
        const stickyRoot = this.querySelector('[data-sticky-bundle]');
        if (!stickyRoot) return;

        const stickyPreviewList = this.querySelector('[data-sticky-preview-list]');
        const stickyTemplate = this.querySelector('[data-sticky-preview-template]');
        const stickyFinalPrice = this.querySelector('[data-sticky-final-price]');
        const stickyPurchaseType = this.querySelector('[data-sticky-purchase-type]');
        const stickyEmptyType = this.querySelector('[data-sticky-empty-type]');
        const stickyAddButton = this.querySelector('[data-sticky-add-to-cart]');
        const stickyPickFlavorsButton = this.querySelector('[data-sticky-pick-flavors]');

        const updateSticky = () => {
            let totalPacks = 0;
            let originalCents = 0;

            this.quantityInputs.forEach((input) => {
                const qty = parseInt(input.value || 0, 10);
                if (qty <= 0) return;

                const card = input.closest('[data-subscription-card]');
                if (!card) return;

                const priceCents = parseInt(card.getAttribute('data-price-cents') || 0, 10);

                totalPacks += qty;
                originalCents += priceCents * qty;
            });

            const subscriptionSelected = this.querySelector('#subscription')?.checked;
            const discountPercent = this.getSubscriptionDiscountPercent(totalPacks);
            const discountedCents = Math.round(originalCents * (1 - discountPercent));
            const finalPriceCents = subscriptionSelected
                ? discountedCents
                : originalCents - this.getBundlePromoFreeCents(totalPacks);

            // Visibility (translate-y / pointer-events) is handled by initStickyVisibility only.

            // Preview list (independent of _previewState)
            if (stickyPreviewList && stickyTemplate) {
                stickyPreviewList.innerHTML = '';

                this.quantityInputs.forEach((input) => {
                    const qty = parseInt(input.value || 0, 10);
                    if (qty <= 0) return;

                    const card = input.closest('[data-subscription-card]');
                    if (!card) return;

                    const imgSrc =
                        card.getAttribute('data-sticky-preview-image');

                    const clone = stickyTemplate.content.cloneNode(true);
                    const img = clone.querySelector('img');
                    const badge = clone.querySelector('[data-quantity-badge]');

                    if (img) img.src = imgSrc;
                    if (badge) badge.textContent = qty;

                    stickyPreviewList.appendChild(clone);
                });

                if (totalPacks === 0) {
                    const thirdCard = this.querySelectorAll('[data-subscription-card]')[3];
                    const defaultImgSrc =
                        thirdCard?.getAttribute('data-sticky-preview-image');

                    if (defaultImgSrc) {
                        const clone = stickyTemplate.content.cloneNode(true);
                        const img = clone.querySelector('img');
                        const badge = clone.querySelector('[data-quantity-badge]');

                        if (img) img.src = defaultImgSrc;
                        if (badge) badge.remove();

                        stickyPreviewList.appendChild(clone);
                    }
                }
            }

            if (stickyFinalPrice) {
                stickyFinalPrice.textContent =
                    totalPacks > 0 ? this.formatMoney(finalPriceCents) : '';
            }

            if (stickyPurchaseType) {
                stickyPurchaseType.textContent =
                    subscriptionSelected ? window.bundleStrings.subscription : window.bundleStrings.one_time_purchase;
            }

            if (stickyPurchaseType && stickyEmptyType) {
                if (totalPacks > 0) {
                    stickyPurchaseType.classList.remove('hidden');
                    stickyEmptyType.classList.add('hidden');
                } else {
                    stickyPurchaseType.classList.add('hidden');
                    stickyEmptyType.classList.remove('hidden');
                }
            }

            if (stickyAddButton) {
                if (totalPacks > 0) stickyAddButton.removeAttribute('disabled');
                else stickyAddButton.setAttribute('disabled', '');
            }

            if (stickyAddButton && stickyPickFlavorsButton) {
                if (totalPacks > 0) {
                    stickyAddButton.classList.remove('hidden');
                    stickyPickFlavorsButton.classList.add('hidden');
                } else {
                    stickyAddButton.classList.add('hidden');
                    stickyPickFlavorsButton.classList.remove('hidden');
                }
            }


        };

        // Observe quantity changes
        this.quantityInputs.forEach((input) => {
            input.addEventListener('change', updateSticky);
            input.addEventListener('input', updateSticky);
        });

        // Observe subscription change
        this.querySelectorAll('input[name="subscription_choise"]').forEach((radio) => {
            radio.addEventListener('change', updateSticky);
        });

        // Sticky CTA
        stickyAddButton?.addEventListener('click', () => this.addSelectedToCart());
        stickyPickFlavorsButton?.addEventListener('click', () => {
            const targetTop = this.getBoundingClientRect().top + window.scrollY - 100;
            window.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
        });

        // Initial sync
        updateSticky();
    }

    initStickyVisibility() {
        const stickyRoot = this.querySelector('[data-sticky-bundle]');
        if (!stickyRoot) return;

        const originalButton = this.querySelector('[data-add-bundle]');
        const footer = document.querySelector('footer');
        if (!originalButton) return;

        const hasScrolledPast = (el) => {
            const rect = el.getBoundingClientRect();
            return rect.bottom <= 0;
        };

        const isOutOfViewport = (el) => {
            const rect = el.getBoundingClientRect();
            return rect.bottom <= 0 || rect.top >= window.innerHeight;
        };

        const updateVisibility = () => {
            const shouldShow =
                hasScrolledPast(originalButton) &&
                (!footer || isOutOfViewport(footer));

            if (shouldShow) {
                stickyRoot.classList.remove('translate-y-full', 'pointer-events-none');
                stickyRoot.classList.add('translate-y-0');
            } else {
                stickyRoot.classList.remove('translate-y-0');
                stickyRoot.classList.add('translate-y-full', 'pointer-events-none');
            }
        };

        window.addEventListener('scroll', updateVisibility, { passive: true });
        window.addEventListener('resize', updateVisibility);

        this.quantityInputs.forEach((input) => {
            input.addEventListener('change', updateVisibility);
            input.addEventListener('input', updateVisibility);
        });

        this.querySelectorAll('input[name="subscription_choise"]').forEach((radio) => {
            radio.addEventListener('change', updateVisibility);
        });

        updateVisibility();
    }

    toggleAddToCartButton() {
        const activeItems = Array.from(this.quantityInputs).filter((item) =>
            parseInt(item.value || 0, 10) > 0
        );

        if (activeItems.length > 0) {
            this.addToCartButton?.removeAttribute('disabled');
        } else {
            this.addToCartButton?.setAttribute('disabled', '');
        }
    }

    onAddToCartClick() {
        this.addSelectedToCart();
    }

    async addSelectedToCart() {
        const quantityInputs = this.querySelectorAll('[data-bundle-quantity]');
        const activeItems = Array.from(quantityInputs).filter((item) =>
            parseInt(item.value || 0, 10) > 0
        );

        if (activeItems.length === 0) return;

        const subscriptionSelected = this.querySelector('#subscription')?.checked;

        document.dispatchEvent(new Event('loading:start'));

        const formData = new FormData();

        activeItems.forEach((item, index) => {
            const card = item.closest('[data-subscription-card]');
            const sellingPlanInput = card?.querySelector('[data-selling-plan-input]');
            const sellingPlanId = sellingPlanInput?.value ?? '';
            const productVariantId = item.getAttribute('data-variant-id');

            formData.append(`items[${index}][id]`, productVariantId);
            formData.append(`items[${index}][quantity]`, item.value);

            if (subscriptionSelected && sellingPlanId) {
                formData.append(`items[${index}][selling_plan]`, sellingPlanId);
            }
        });

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        try {
            const response = await fetch(routes.cart_add_url, config);
            const data = await response.json();

            if (data.status) {
                if (typeof this.handleErrorMessage === 'function') {
                    this.handleErrorMessage(data.description);
                }
                return;
            }

            window.dispatchEvent(
                new CustomEvent('cart:update', {
                    detail: {
                        source: 'bundle-builder',
                        cartData: data,
                    },
                })
            );

            this.quantityInputs.forEach((input) => {
                input.value = 0;
                input.dispatchEvent(new Event('change', { bubbles: true }));
            });
        } catch (e) {
            console.error('[BundleBuilder] Failed to add items to cart', e);
        }

        document.dispatchEvent(new Event('loading:end'));
    }
}

customElements.define('bundle-builder', BundleBuilder);